import { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Container, Reveal } from '@/components/ui'
import { H2 } from '@/components/ui/typography'
import type { MagazineIssue } from '@/content/magazines'
import { unifiedMagazinesFrom } from '@/content/magazinesUnified'
import { useCmsMagazines } from '@/hooks/useCmsMagazines'
import { useCmsMediaIndex } from '@/hooks/useCmsMediaIndex'
import { safeExternalUrl } from '@/utils/safeExternalUrl'

/**
 * MagazineFeature — the homepage 媒體報導 slider.
 *
 * Shows up to MAX_SLIDES press items from the same unified, CMS-driven list
 * the /magazine archive uses, auto-flipping on a gentle interval (paused on
 * hover and for prefers-reduced-motion users). The primary CTA per slide
 * follows the article kind: PDF entries deep-link to the in-app viewer at
 * /magazine, image entries open their /magazine/:slug detail page, and
 * external / article-link entries open the publisher site in a new tab.
 *
 * `settings.slug` from the CMS's 「首頁 sections」 editor is still honoured:
 * when it names an issue in the list, that issue is rotated to the front so
 * it becomes the slide a visitor lands on. Empty keeps the default of newest
 * first.
 */

/** Hard cap requested for the homepage carousel. */
const MAX_SLIDES = 6

/** Autoplay cadence, matching the hero slider. */
const AUTOPLAY_MS = 6000

export default function MagazineFeature({ settings }: { settings?: { slug?: string } }) {
  const { t } = useTranslation()
  const slug = typeof settings?.slug === 'string' ? settings.slug : ''
  const magazines = useCmsMagazines()
  const mediaIndex = useCmsMediaIndex()

  // The unified list, capped at MAX_SLIDES. `settings.slug` rotates the
  // featured issue to slide 1; a stale slug (or the empty default) keeps the
  // archive's own newest-first ordering.
  const items = (() => {
    const all = unifiedMagazinesFrom(magazines, mediaIndex).slice(0, MAX_SLIDES)
    if (!slug) return all
    const idx = all.findIndex((m) => m.slug === slug)
    if (idx <= 0) return all
    return [all[idx], ...all.slice(0, idx), ...all.slice(idx + 1)]
  })()

  const isLoop = items.length > 1
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: isLoop, duration: 28 })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [autoplay, setAutoplay] = useState(true)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
  }, [emblaApi, onSelect])

  // Autoplay — skipped when the user hovers (matches the hero) or when they
  // prefer reduced motion; the slider is still fully manual for them.
  useEffect(() => {
    if (!emblaApi || !autoplay || !isLoop) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const id = setInterval(() => emblaApi.scrollNext(), AUTOPLAY_MS)
    return () => clearInterval(id)
  }, [emblaApi, autoplay, isLoop])

  const scrollTo = useCallback(
    (i: number) => emblaApi && emblaApi.scrollTo(i),
    [emblaApi],
  )
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  if (items.length === 0) return null

  return (
    <section
      className="py-section-y px-section-x bg-surface-2"
      aria-label={t('home.press.tag')}
      onMouseEnter={() => setAutoplay(false)}
      onMouseLeave={() => setAutoplay(true)}
    >
      <Container>
        <Reveal>
          <div
            ref={emblaRef}
            className="overflow-hidden rounded-yada-xl"
          >
            <div className="flex">
              {items.map((mag, idx) => (
                <MagazineFeatureSlide
                  key={mag.slug}
                  mag={mag}
                  index={idx}
                  count={items.length}
                />
              ))}
            </div>
          </div>
        </Reveal>

        {/* Controls: prev / dots / next, plus the archive link. Autoplay
            resumes when the pointer leaves the section, so these only need
            to stop the interval for the current interaction. */}
        {isLoop && (
          <div className="flex flex-col items-center gap-4 mt-7">
            <div className="flex items-center gap-4">
              <button
                type="button"
                aria-label="Previous slide"
                onClick={scrollPrev}
                className="inline-flex h-9 w-9 items-center justify-center rounded-yada-full border border-border bg-surface text-primary hover:bg-primary hover:text-surface transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
              >
                <Icon icon="lucide:chevron-left" className="text-lg" />
              </button>
              <div className="flex justify-center gap-2" role="tablist" aria-label={t('home.press.tag')}>
                {items.map((mag, idx) => (
                  <button
                    key={mag.slug}
                    role="tab"
                    aria-selected={idx === selectedIndex}
                    aria-label={mag.title}
                    onClick={() => scrollTo(idx)}
                    className={`h-2 rounded-full transition-all ${
                      idx === selectedIndex
                        ? 'w-10 bg-primary'
                        : 'w-2 bg-ink-soft/30 hover:bg-ink-soft/50'
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                aria-label="Next slide"
                onClick={scrollNext}
                className="inline-flex h-9 w-9 items-center justify-center rounded-yada-full border border-border bg-surface text-primary hover:bg-primary hover:text-surface transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
              >
                <Icon icon="lucide:chevron-right" className="text-lg" />
              </button>
            </div>

            <Link
              to="/magazine"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-dark transition-colors"
            >
              <Icon icon="lucide:layout-grid" />
              {t('home.press.viewAll')}
            </Link>
          </div>
        )}
        {!isLoop && items.length === 1 && (
          <div className="flex justify-center mt-7">
            <Link
              to="/magazine"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-dark transition-colors"
            >
              <Icon icon="lucide:layout-grid" />
              {t('home.press.viewAll')}
            </Link>
          </div>
        )}
      </Container>
    </section>
  )
}

/**
 * One slide of the magazine feature carousel — the same 2/3 editorial split
 * the standalone version used, slightly compacted so six entries stack into
 * a carousel without the section ballooning in height.
 *
 * The cover frame uses a FIXED height (not aspect-ratio) so consecutive
 * slides — landscape HK01 scans, 2:1 Ming Pao clippings — can't change the
 * section height between flips; everything is contained within the frame.
 */
function MagazineFeatureSlide({
  mag,
  index,
  count,
}: {
  mag: MagazineIssue
  index: number
  count: number
}) {
  const { t, i18n } = useTranslation()
  const publicationLabel = i18n.language.startsWith('zh') ? mag.publication : mag.publicationEn
  const issueLabel = i18n.language.startsWith('zh') ? mag.issue : mag.issueEn

  // Where the primary "read article" CTA leads, per kind. `image` entries go
  // to their detail page (better than jumping straight to the publisher);
  // pdf deep-links its viewer anchor; everything else opens externally.
  const isPdf = mag.kind === 'pdf'
  const isImage = mag.kind === 'image'
  // The external URL comes from the CMS, so it is validated before it is
  // allowed to become an href — a `javascript:` value would otherwise run
  // script in every visitor's browser. A value that fails the check
  // degrades to the magazine's own page instead of becoming a dead (or
  // executable) link.
  const externalHref = safeExternalUrl(mag.externalUrl)
  const primaryHref = isPdf
    ? `/magazine#${mag.slug}-viewer`
    : isImage
      ? `/magazine/${mag.slug}`
      : (externalHref ?? `/magazine/${mag.slug}`)
  // `primaryIsExternal` describes the href we actually emit: once an
  // unsafe external URL has fallen back to the internal page, the link is
  // internal and must not open a new tab.
  const primaryIsExternal = !isPdf && !isImage && externalHref !== undefined
  const primaryLabel = primaryIsExternal
    ? t('home.press.openExternal')
    : t('home.press.readArticle')
  const primaryIcon = primaryIsExternal ? 'lucide:external-link' : 'lucide:book-open-text'

  const coverAlt = t('magazine.coverAlt', { publication: publicationLabel, issue: issueLabel })

  return (
    <div
      className="flex-[0_0_100%] min-w-0"
      role="group"
      aria-roledescription="slide"
      aria-label={`Slide ${index + 1} of ${count}`}
    >
      <div className="grid lg:grid-cols-5 gap-6 lg:gap-gap-cards items-center">
        {/* Cover poster — fixed-height frame keeps every slide the same
            height, so the auto-flip never makes the section jump. */}
        <div className="lg:col-span-2">
          <a
            href={primaryHref}
            target={primaryIsExternal ? '_blank' : undefined}
            rel={primaryIsExternal ? 'noopener noreferrer' : undefined}
            aria-label={`${publicationLabel} ${issueLabel}`}
            className="flex h-72 md:h-96 items-center justify-center mx-auto max-w-[280px] sm:max-w-sm focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-lg"
          >
            {mag.coverImage && (
              <img
                src={mag.coverImage}
                alt={coverAlt}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
                width={mag.coverWidth}
                height={mag.coverHeight}
                className="max-h-full max-w-full w-auto h-auto object-contain rounded-yada-lg shadow-yada-lg border border-border transition-transform hover:-translate-y-1 hover:shadow-yada-xl"
              />
            )}
          </a>
        </div>

        {/* Editorial copy */}
        <div className="lg:col-span-3">
          <span className="section-title__tagline inline-block mb-3">
            {t('home.press.tag')}
          </span>
          <H2
            id={`press-feature-title-${index}`}
            className="mb-3 text-balance"
          >
            {mag.interviewee ? `${mag.interviewee} · ` : ''}
            {mag.title}
          </H2>
          <p className="text-sm md:text-base text-ink-muted font-medium mb-3 md:mb-4">
            {publicationLabel}
            {issueLabel ? ` · ${issueLabel}` : ''}
          </p>
          <p className="text-base md:text-lg text-ink-soft leading-relaxed mb-6 text-pretty line-clamp-4">
            {mag.summary}
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-5 text-xs sm:text-sm text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <Icon icon="lucide:calendar" className="text-base" />
              <time dateTime={mag.date}>{mag.date}</time>
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
            <a
              href={primaryHref}
              target={primaryIsExternal ? '_blank' : undefined}
              rel={primaryIsExternal ? 'noopener noreferrer' : undefined}
              className="inline-flex items-center justify-center gap-2 bg-primary text-surface px-6 py-3 rounded-yada-full font-semibold hover:bg-primary-dark transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 w-full sm:w-auto"
            >
              <Icon icon={primaryIcon} />
              {primaryLabel}
              <Icon icon={primaryIsExternal ? 'lucide:arrow-up-right' : 'lucide:arrow-right'} />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
